import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, X, AlertCircle, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Dropdown from '@/components/ui/Dropdown';
import { cn } from '@/lib/utils';
import { slideUp, fadeIn } from '@/lib/animations';
import useAuth from '../hooks/useAuth';
import apiService from '../services/api';
import { 
  Project, 
  ProjectUpdateData, 
  DesiredTimeframe, 
  TargetMarket, 
  ToneNew, 
  FormatType 
} from '../types/project';

interface ContentType {
  id: string;
  name: string;
  description: string;
  icon?: string;
}

const ProjectEdit: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [project, setProject] = useState<Project | null>(null);
  const [formData, setFormData] = useState<ProjectUpdateData>({
    name: '',
    description: '',
    content_type_id: '',
    tone_new: ToneNew.Inspirational,
    desired_timeframe: DesiredTimeframe.OneMinute,
    target_market: TargetMarket.Neutral,
    format_type: FormatType.ExplainerBurst
  });
  
  const [contentTypes, setContentTypes] = useState<ContentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingContentTypes, setLoadingContentTypes] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  // Fetch project details
  const fetchProject = useCallback(async () => {
    if (!projectId) return;
    
    try {
      setError(null);
      console.log('[PROJECT_EDIT] Fetching project:', projectId);
      
      const response = await apiService.getProjectById(projectId);
      
      if (response.data?.status === 'success' && response.data.data) {
        const projectData = response.data.data;
        setProject(projectData);
        
        // Populate form data
        setFormData({
          name: projectData.name,
          description: projectData.description || '',
          content_type_id: projectData.content_type_id,
          tone_new: projectData.tone_new || ToneNew.Inspirational,
          desired_timeframe: projectData.desired_timeframe || DesiredTimeframe.OneMinute,
          target_market: projectData.target_market || TargetMarket.Neutral,
          format_type: projectData.format_type || FormatType.ExplainerBurst
        });
      } else {
        throw new Error('Project not found');
      }
    } catch (error: any) {
      console.error('[PROJECT_EDIT] Error:', error);
      setError(error.message || 'Failed to load project');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // Fetch content types
  const fetchContentTypes = useCallback(async () => {
    try {
      setLoadingContentTypes(true);
      const response = await apiService.getContentTypes();
      
      if (response.data?.status === 'success' && response.data.data?.contentTypes) {
        setContentTypes(response.data.data.contentTypes);
      } else {
        throw new Error('Failed to fetch content types');
      }
    } catch (error: any) {
      console.error('[CONTENT_TYPES] Error fetching:', error);
      setError('Failed to load content types');
    } finally {
      setLoadingContentTypes(false);
    }
  }, []);

  // Define dropdown options
  const timeframeOptions = [
    { value: DesiredTimeframe.ThirtySeconds, label: '30 seconds', description: 'Quick, punchy content perfect for social media' },
    { value: DesiredTimeframe.OneMinute, label: '1 minute', description: 'Short-form content with focused messaging' },
    { value: DesiredTimeframe.TwoMinutes, label: '2 minutes', description: 'Medium-length content for detailed explanations' },
    { value: DesiredTimeframe.FiveMinutes, label: '5 minutes', description: 'Longer-form content for comprehensive coverage' },
    { value: DesiredTimeframe.FifteenMinutes, label: '15 minutes', description: 'Extended content for in-depth analysis' },
    { value: DesiredTimeframe.More, label: 'More', description: 'Custom length or extended content' }
  ];

  const targetMarketOptions = [
    { value: TargetMarket.Progressive, label: 'Progressive', description: 'Forward-thinking, innovative, and open to new ideas' },
    { value: TargetMarket.Neutral, label: 'Neutral', description: 'Balanced, objective, and appeals to a broad audience' },
    { value: TargetMarket.Conservative, label: 'Conservative', description: 'Traditional values, established approaches, and proven methods' }
  ];

  const toneOptions = [
    { value: ToneNew.Poetic, label: 'Poetic', description: 'Artistic, lyrical, and emotionally evocative content' },
    { value: ToneNew.Inspirational, label: 'Inspirational', description: 'Motivating, uplifting, and empowering messaging' },
    { value: ToneNew.Reverent, label: 'Reverent', description: 'Respectful, dignified, and honorific tone' }
  ];

  const formatOptions = [
    { value: FormatType.BiographicMontage, label: 'Biographic Montage', description: 'Life story compilation with emotional narrative' },
    { value: FormatType.NewReport, label: 'News Report', description: 'Factual, journalistic style with current events focus' },
    { value: FormatType.ExplainerBurst, label: 'Explainer Burst', description: 'Quick, educational content that breaks down complex topics' },
    { value: FormatType.OpinionSnap, label: 'Opinion Snap', description: 'Personal viewpoint with persuasive elements' },
    { value: FormatType.Commercial, label: 'Commercial', description: 'Promotional content designed to drive action' },
    { value: FormatType.TeaserTrailer, label: 'Teaser Trailer', description: 'Preview content that builds anticipation' }
  ];

  useEffect(() => {
    if (projectId) {
      fetchProject();
      fetchContentTypes();
    }
  }, [projectId, fetchProject, fetchContentTypes]);

  const handleInputChange = (field: keyof ProjectUpdateData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Project name is required';
    }
    
    if (!formData.content_type_id) {
      errors.content_type_id = 'Content type is required';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!projectId || !validateForm()) {
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      
      console.log('[PROJECT_EDIT] Updating project:', formData);
      const response = await apiService.updateProject(projectId, formData);
      
      if (response.data?.status === 'success') {
        console.log('[PROJECT_EDIT] Project updated successfully');
        // Navigate back to project detail
        navigate(`/projects/${projectId}`);
      } else {
        throw new Error('Failed to update project');
      }
    } catch (error: any) {
      console.error('[PROJECT_EDIT] Error:', error);
      setError(error.message || 'Failed to update project');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/projects/${projectId}`);
  };

  const hasUnsavedChanges = project && (
    formData.name !== project.name ||
    formData.description !== (project.description || '') ||
    formData.content_type_id !== project.content_type_id ||
    formData.tone_new !== project.tone_new ||
    formData.desired_timeframe !== project.desired_timeframe ||
    formData.target_market !== project.target_market ||
    formData.format_type !== project.format_type
  );

  if (loading || loadingContentTypes) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading project...</p>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <AlertCircle className="h-12 w-12 mx-auto" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Failed to Load Project</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <div className="space-x-2">
            <Button onClick={fetchProject}>Try Again</Button>
            <Button variant="outline" onClick={() => navigate('/projects')}>Back to Projects</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className={cn(slideUp(), "mb-8")}>
          <Button
            variant="ghost"
            onClick={handleCancel}
            className="mb-4 -ml-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Project
          </Button>
          
          <div className="flex items-center mb-4">
            <Settings className="h-6 w-6 text-primary mr-2" />
            <h1 className="text-3xl font-bold tracking-tight">Edit Project</h1>
          </div>
          <p className="text-lg text-muted-foreground">
            Update your project settings and preferences.
          </p>
        </div>

        {/* Form */}
        <div className={cn(fadeIn(200), "bg-background rounded-xl shadow-sm border border-border p-6")}>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Project Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium mb-2">
                Project Name *
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={cn(
                  "w-full px-3 py-2 border rounded-md bg-background",
                  validationErrors.name ? "border-red-500" : "border-input"
                )}
                placeholder="Enter your project name"
                disabled={saving}
              />
              {validationErrors.name && (
                <p className="mt-1 text-sm text-red-500">{validationErrors.name}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium mb-2">
                Description
              </label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md bg-background"
                placeholder="Describe your project (optional)"
                rows={3}
                disabled={saving}
              />
            </div>

            {/* Content Type */}
            <Dropdown
              label="Content Type"
              value={formData.content_type_id}
              options={contentTypes.map(type => ({
                value: type.id,
                label: type.name,
                description: type.description
              }))}
              onChange={(value) => handleInputChange('content_type_id', value)}
              error={validationErrors.content_type_id}
              required
              placeholder="Select a content type"
              disabled={saving}
            />

            {/* Tone */}
            <Dropdown
              label="Tone"
              value={formData.tone_new}
              options={toneOptions}
              onChange={(value) => handleInputChange('tone_new', value)}
              error={validationErrors.tone_new}
              required
              placeholder="Select a tone"
              disabled={saving}
            />

            {/* Desired Timeframe */}
            <Dropdown
              label="Desired Timeframe"
              value={formData.desired_timeframe}
              options={timeframeOptions}
              onChange={(value) => handleInputChange('desired_timeframe', value)}
              error={validationErrors.desired_timeframe}
              required
              placeholder="Select desired timeframe"
              disabled={saving}
            />

            {/* Target Market */}
            <Dropdown
              label="Target Market"
              value={formData.target_market}
              options={targetMarketOptions}
              onChange={(value) => handleInputChange('target_market', value)}
              error={validationErrors.target_market}
              required
              placeholder="Select target market"
              disabled={saving}
            />

            {/* Format */}
            <Dropdown
              label="Format"
              value={formData.format_type}
              options={formatOptions}
              onChange={(value) => handleInputChange('format_type', value)}
              error={validationErrors.format_type}
              required
              placeholder="Select format type"
              disabled={saving}
            />

            {/* Warning for unsaved changes */}
            {hasUnsavedChanges && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-700">
                  You have unsaved changes. Make sure to save before leaving this page.
                </p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={saving}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving || !hasUnsavedChanges}
              >
                {saving ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent mr-2" />
                    Saving...
                  </div>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>

        {/* Project Info */}
        <div className={cn(fadeIn(400), "mt-6 text-center")}>
          <p className="text-sm text-muted-foreground">
            Project created on {new Date(project.created_at).toLocaleDateString()}
            {project.videos && project.videos.length > 0 && (
              <> • {project.videos.length} video{project.videos.length !== 1 ? 's' : ''} uploaded</>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProjectEdit; 